/**
 * `<plp-map>` — обёртка над Yandex/Google Maps SDK.
 *
 * Сам тег лёгкий: показывает loader, лениво подгружает map-чанк, который
 * инжектит SDK провайдера и рендерит карту.
 *
 * API-ключи рендерятся на странице через `window.__plpConfig` (см. SPEC-wc-03).
 */

import { isEditMode } from '../core/edit-mode.js';
import { loadChunk } from '../core/lazy.js';
import { t } from '../i18n/index.js';

export interface MapRuntime {
  render(host: PlpMap, opts: PlpMapOptions): Promise<void>;
}

export interface PlpMapOptions {
  provider: 'yandex' | 'google';
  address?: string;
  coords?: string;
  zoom: number;
  marker: boolean;
  staticOnly: boolean;
}

declare global {
  interface Window {
    __plpRuntime?: { map?: MapRuntime } & Record<string, unknown>;
  }
}

export class PlpMap extends HTMLElement {
  static get observedAttributes(): string[] {
    return ['provider', 'address', 'coords', 'zoom', 'marker'];
  }

  private _initialized = false;

  connectedCallback(): void {
    if (this._initialized) return;
    this._initialized = true;

    this.style.display = this.style.display || 'block';
    this.style.minHeight = this.style.minHeight || '240px';
    this.style.position = this.style.position || 'relative';
    this.renderLoader();

    void this.boot();
  }

  attributeChangedCallback(): void {
    if (!this._initialized || !this.isConnected) return;
    void this.boot();
  }

  private async boot(): Promise<void> {
    try {
      await loadChunk('map');
      const runtime = window.__plpRuntime?.map;
      if (!runtime) return;
      await runtime.render(this, this.options());
    } catch {
      this.renderError();
    }
  }

  private options(): PlpMapOptions {
    const provider = (this.getAttribute('provider') ?? 'yandex').toLowerCase();
    return {
      provider: provider === 'google' ? 'google' : 'yandex',
      address: this.getAttribute('address') ?? undefined,
      coords: this.getAttribute('coords') ?? undefined,
      zoom: parseInt(this.getAttribute('zoom') ?? '15', 10) || 15,
      marker: this.getAttribute('marker') !== 'off',
      staticOnly: isEditMode(),
    };
  }

  private renderLoader(): void {
    if (this.querySelector(':scope > .plp-map-loader')) return;
    const loader = document.createElement('div');
    loader.className =
      'plp-map-loader absolute inset-0 flex items-center justify-center text-sm text-gray-500';
    loader.textContent = t('map.loading');
    this.appendChild(loader);
  }

  private renderError(): void {
    this.querySelector(':scope > .plp-map-loader')?.remove();
    const err = document.createElement('div');
    err.className =
      'plp-map-error absolute inset-0 flex items-center justify-center text-sm text-rose-600 bg-rose-50';
    err.textContent = t('map.loadError');
    this.appendChild(err);
  }
}
